import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertCircle, AlertTriangle, Check, X, FileText, MapPin, User, Phone, Building2, Loader2, ExternalLink, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';
import ParsedOrderViewModal from './ParsedOrderViewModal';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';

// Encon brand colors (kept identical to the classic Transport View entry modal)
const enconOrange = '#ee7203';
const enconBlack = '#1d1d1b';

/**
 * The EXACT entry-details modal used by the classic Transport View page
 * (`ViewModeGrid.jsx`) — entry details / status / photo / service issue on
 * the left, with the parsed order sliding in on the right. Extracted here
 * so the New Transport UI (Beta) can open the identical modal on entry
 * click, instead of a bespoke one-off.
 */
const TransportEntryDetailModal = ({ isOpen, entry, vehicle, runId, selectedDate, user, onClose }) => {
  const navigate = useNavigate();
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  const [viewParsedOrder, setViewParsedOrder] = useState(null);
  const [viewParsedOrderLoading, setViewParsedOrderLoading] = useState(false);

  const [showTransferDetails, setShowTransferDetails] = useState(false);
  const [transferDetailsBranch, setTransferDetailsBranch] = useState(null);

  const [serviceIssue, setServiceIssue] = useState(null);
  const [showServiceIssueSection, setShowServiceIssueSection] = useState(false);
  const [serviceIssueCategory, setServiceIssueCategory] = useState('');
  const [serviceIssueNotes, setServiceIssueNotes] = useState('');

  const [entryPhoto, setEntryPhoto] = useState(null);
  const [showPhotoView, setShowPhotoView] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);

  const userPermissions = user || null;
  // Accepts either a Date object (classic usage) or an already-formatted 'yyyy-MM-dd' string
  // (New Transport UI, which keeps its selected date as a string throughout).
  const dateStr = selectedDate instanceof Date ? format(selectedDate, 'yyyy-MM-dd') : (selectedDate || '');

  // An entry can have more than one order number attached (e.g. a merged delivery) —
  // `order_number` is the primary, `order_numbers` holds any additional ones.
  const allOrderNumbers = [selectedEntry?.order_number, ...(selectedEntry?.order_numbers || [])]
    .filter((n) => n && String(n).trim());
  const handleNavigateOrder = (num) => {
    setViewParsedOrderLoading(true);
    api.get(`${API}/automations/orders/${num}`)
      .then((res) => setViewParsedOrder(res.data))
      .catch(() => toast.error(`Could not load order #${num}`))
      .finally(() => setViewParsedOrderLoading(false));
  };

  // Load everything for the clicked entry — mirrors classic `openEntryModal`.
  useEffect(() => {
    if (!isOpen || !entry) return;
    setSelectedEntry(entry);
    setLinkedCustomer(null);
    setViewParsedOrder(null);

    if (entry.embedded_order) {
      setViewParsedOrder(entry.embedded_order);
    } else {
      const orderNum = (entry.order_number || '').trim();
      if (orderNum) {
        setViewParsedOrderLoading(true);
        api.get(`${API}/automations/orders/${orderNum}`)
          .then((res) => setViewParsedOrder(res.data))
          .catch(() => {})
          .finally(() => setViewParsedOrderLoading(false));
      }
    }

    if (entry.photo_id) {
      setPhotoLoading(true);
      api.get(`${API}/photos/${entry.photo_id}?format=base64`)
        .then((res) => setEntryPhoto(res.data.photo))
        .catch(() => setEntryPhoto(null))
        .finally(() => setPhotoLoading(false));
    } else if (entry.photo) {
      setEntryPhoto(entry.photo);
    } else {
      setEntryPhoto(null);
    }

    api.get(`${API}/service-issues/entry/${entry.id}`)
      .then((res) => {
        if (res.data.service_issue) {
          setServiceIssue(res.data.service_issue);
          setServiceIssueCategory(res.data.service_issue.category || '');
          setServiceIssueNotes(res.data.service_issue.notes || '');
          setShowServiceIssueSection(true);
        } else {
          setServiceIssue(null);
          setServiceIssueCategory('');
          setServiceIssueNotes('');
          setShowServiceIssueSection(false);
        }
      })
      .catch(() => {
        setServiceIssue(null);
        setServiceIssueCategory('');
        setServiceIssueNotes('');
        setShowServiceIssueSection(false);
      });

    const customerName = entry.customer_name || entry.customer;
    const orderNumber = entry.order_number || entry.id;
    if (customerName) {
      setLoadingCustomer(true);
      const url = orderNumber
        ? `${API}/customers/by-name/${encodeURIComponent(customerName)}?order_number=${encodeURIComponent(orderNumber)}`
        : `${API}/customers/by-name/${encodeURIComponent(customerName)}`;
      api.get(url)
        .then((res) => { if (res.data.found && res.data.customer) setLinkedCustomer(res.data.customer); })
        .catch(() => {})
        .finally(() => setLoadingCustomer(false));
    }
  }, [isOpen, entry?.id]);

  // Fetch branch details for the Transfer Details sub-modal (postcode/phone fallback)
  useEffect(() => {
    if (!showTransferDetails || !selectedEntry?.linked_transfers?.length) return;
    const first = selectedEntry.linked_transfers[0];
    const branchCode = first.collection_branch_id || first.branch_id || first.destination_branch_id || first.origin_branch_id;
    if (!branchCode) return;
    (async () => {
      try {
        const res = await fetch(`${API}/branch-directory`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const list = await res.json();
          const code = String(branchCode).toLowerCase();
          const match = (list || []).find((b) =>
            String(b.id || '').toLowerCase() === code ||
            String(b.branchCode || '').toLowerCase() === code ||
            String(b.name || '').toLowerCase() === code
          );
          if (match) setTransferDetailsBranch(match);
        }
      } catch (_) { /* ignore */ }
    })();
  }, [showTransferDetails, selectedEntry]);

  const buildUpdatedVehicle = (mutateEntry) => {
    let updatedVehicle = { ...vehicle };
    if (runId) {
      updatedVehicle.runs = vehicle.runs.map((run) => (
        run.id === runId ? { ...run, entries: run.entries.map((e) => (e.id === selectedEntry.id ? mutateEntry(e) : e)) } : run
      ));
    } else {
      updatedVehicle.entries = vehicle.entries.map((e) => (e.id === selectedEntry.id ? mutateEntry(e) : e));
    }
    return updatedVehicle;
  };

  const updateEntryStatus = async (newStatus) => {
    if (!selectedEntry || !vehicle) return;
    try {
      const updatedVehicle = buildUpdatedVehicle((e) => (
        e.status === 'collection' && newStatus === 'completed' ? { ...e, status: 'collection', collected: true } : { ...e, status: newStatus }
      ));
      await api.put(`${API}/logs/${dateStr}/vehicle/${vehicle.id}`, { ...updatedVehicle, _save_date: dateStr });
      setSelectedEntry({ ...selectedEntry, status: newStatus });
      toast.success('Status updated', { duration: 1500 });
      window.location.reload();
      onClose();
    } catch (error) {
      console.error('Error updating entry status:', error);
      toast.error('Failed to update status');
    }
  };

  const updateEntryNotes = async (newNotes) => {
    if (!selectedEntry || !vehicle) return;
    try {
      const updatedVehicle = buildUpdatedVehicle((e) => ({ ...e, status_note: newNotes }));
      await api.put(`${API}/logs/${dateStr}/vehicle/${vehicle.id}`, updatedVehicle);
      toast.success('Note saved! Refreshing...', { duration: 1500 });
      onClose();
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to save notes', { duration: 2000 });
    }
  };

  const saveServiceIssue = async () => {
    if (!selectedEntry || !vehicle) return;
    if (!serviceIssueCategory) { toast.error('Please select a category'); return; }
    try {
      const issueData = { entry_id: selectedEntry.id, date: dateStr, vehicle_id: vehicle.id, category: serviceIssueCategory, notes: serviceIssueNotes };
      if (serviceIssueCategory === 'CLEAR') {
        if (serviceIssue && serviceIssue.id) {
          await api.delete(`${API}/service-issues/${serviceIssue.id}`);
          setServiceIssue(null);
          toast.success('Service issue cleared!');
        } else {
          toast.info('No service issue to clear');
        }
      } else if (serviceIssue && serviceIssue.id) {
        await api.put(`${API}/service-issues/${serviceIssue.id}`, issueData);
        toast.success('Service issue updated!');
      } else {
        const response = await api.post(`${API}/service-issues`, issueData);
        setServiceIssue({ id: response.data.id, ...issueData });
        toast.success('Service issue created!');
      }
    } catch (error) {
      console.error('Error saving service issue:', error);
      toast.error('Failed to save service issue');
    }
  };

  const triggerPhotoInput = () => document.getElementById('transport-entry-photo-upload-input').click();

  const uploadPhotoToBackend = async (photoData) => {
    if (!selectedEntry || !vehicle) return;
    try {
      const uploadResponse = await api.post(`${API}/photos/upload`, {
        photo: photoData,
        entry_id: selectedEntry.id,
        date: dateStr,
        metadata: { customer_name: selectedEntry.customer_name, order_number: selectedEntry.order_number, vehicle_id: vehicle.id }
      });
      const photoId = uploadResponse.data.photo_id;
      const response = await api.get(`${API}/logs/${dateStr}`);
      const dailyLog = response.data;
      const updatedVehicles = dailyLog.vehicles.map((v) => {
        if (v.id !== vehicle.id) return v;
        let updatedVehicle = { ...v };
        if (runId) {
          updatedVehicle.runs = v.runs.map((run) => (
            run.id === runId ? { ...run, entries: run.entries.map((e) => (e.id === selectedEntry.id ? { ...e, photo_id: photoId, photo: null } : e)) } : run
          ));
        } else {
          updatedVehicle.entries = v.entries.map((e) => (e.id === selectedEntry.id ? { ...e, photo_id: photoId, photo: null } : e));
        }
        return updatedVehicle;
      });
      await api.put(`${API}/logs/${dateStr}`, { date: dateStr, vehicles: updatedVehicles });
      setSelectedEntry({ ...selectedEntry, photo_id: photoId, photo: null });
      toast.success('Photo saved! Refreshing...', { duration: 1500 });
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to save photo');
    }
  };

  const deletePhotoFromBackend = async () => {
    if (!selectedEntry?.photo_id) return;
    try {
      await api.delete(`${API}/photos/${selectedEntry.photo_id}`);
      const response = await api.get(`${API}/logs/${dateStr}`);
      const dailyLog = response.data;
      const updatedVehicles = dailyLog.vehicles.map((v) => {
        if (v.id !== vehicle.id) return v;
        let updatedVehicle = { ...v };
        if (runId) {
          updatedVehicle.runs = v.runs.map((run) => (
            run.id === runId ? { ...run, entries: run.entries.map((e) => (e.id === selectedEntry.id ? { ...e, photo_id: null, photo: null } : e)) } : run
          ));
        } else {
          updatedVehicle.entries = v.entries.map((e) => (e.id === selectedEntry.id ? { ...e, photo_id: null, photo: null } : e));
        }
        return updatedVehicle;
      });
      await api.put(`${API}/logs/${dateStr}`, { date: dateStr, vehicles: updatedVehicles });
      setSelectedEntry({ ...selectedEntry, photo_id: null, photo: null });
      toast.success('Photo removed! Refreshing...', { duration: 1500 });
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setPhotoLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      setEntryPhoto(reader.result);
      await uploadPhotoToBackend(reader.result);
      setPhotoLoading(false);
    };
    reader.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { setViewParsedOrder(null); onClose(); } }}>
        <DialogContent
          hideCloseButton
          className="bg-white p-0"
          data-testid="transport-entry-detail-modal"
          overlayClassName={modalUiV2 ? 'bg-black/50' : undefined}
          style={{
            position: 'fixed',
            top: modalUiV2 ? `calc(${activityBar.headerHeight}px + 16px)` : (viewParsedOrder ? '4%' : '50%'),
            left: '50%',
            transform: modalUiV2 ? 'translateX(-50%)' : (viewParsedOrder ? 'translateX(-50%)' : 'translate(-50%, -50%)'),
            margin: 0,
            maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '92vh',
            width: viewParsedOrder ? 'min(98vw, 1200px)' : 'min(92vw, 520px)',
            maxWidth: viewParsedOrder ? 'min(98vw, 1200px)' : 'min(92vw, 520px)',
            transition: 'width 320ms cubic-bezier(0.4,0,0.2,1), max-width 320ms cubic-bezier(0.4,0,0.2,1), top 320ms cubic-bezier(0.4,0,0.2,1), transform 320ms cubic-bezier(0.4,0,0.2,1)',
            border: '2px solid #ee7203',
            borderRadius: '1.5rem',
            overflow: 'hidden',
            display: 'flex',
            boxShadow: '0 25px 60px rgba(238,114,3,0.15), 0 8px 32px rgba(0,0,0,0.12)',
          }}
        >
          {/* ── Left: entry details ─────────────────── */}
          <div className="flex-shrink-0 overflow-y-auto" style={{ width: '100%', minWidth: viewParsedOrder ? '420px' : 'unset', maxWidth: viewParsedOrder ? '420px' : 'unset' }}>
            {selectedEntry && (
              <div className="space-y-2 md:space-y-3 p-4 md:p-6">
                {/* Header Row: Order Number (centered) + Close Button */}
                <div className="flex items-center justify-between -mt-2 mb-1 gap-2">
                  <div className="flex items-center min-w-[28px] z-10">
                    {selectedEntry.linked_transfers && selectedEntry.linked_transfers.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(() => setShowTransferDetails(true), 0); }}
                        className="relative z-20 flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs font-semibold transition-colors whitespace-nowrap"
                        data-testid="transport-new-view-show-transfer-details-btn"
                        title="Show Transfer Details"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Transfer
                      </button>
                    )}
                  </div>
                  <div className="text-center flex-1 pointer-events-none">
                    <div className="inline-block px-4 py-1.5 rounded-full text-white font-bold text-lg" style={{ backgroundColor: enconOrange }}>
                      #{selectedEntry.order_number || selectedEntry.id || 'N/A'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!viewParsedOrder && (viewParsedOrderLoading || selectedEntry.order_number || selectedEntry.embedded_order) && (
                      <button
                        onClick={() => {
                          const orderNum = (selectedEntry.order_number || '').trim();
                          if (!viewParsedOrder && orderNum && !viewParsedOrderLoading) {
                            setViewParsedOrderLoading(true);
                            api.get(`${API}/automations/orders/${orderNum}`)
                              .then((res) => setViewParsedOrder(res.data))
                              .catch(() => {})
                              .finally(() => setViewParsedOrderLoading(false));
                          }
                        }}
                        className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        style={{ backgroundColor: '#f3f4f6', color: enconOrange }}
                        title="View parsed order"
                        data-testid="transport-new-view-order-eye-btn"
                      >
                        {viewParsedOrderLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => { setViewParsedOrder(null); onClose(); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: enconOrange }}
                      data-testid="transport-new-view-modal-close-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {selectedEntry.postcode && (
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                      <MapPin className="w-3.5 h-3.5" style={{ color: enconOrange }} />
                      {selectedEntry.postcode}
                    </span>
                  </div>
                )}

                {selectedEntry.redelivery_note && (
                  <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs md:text-sm text-red-800" data-testid="transport-new-view-redelivery-note">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{selectedEntry.redelivery_note}</span>
                  </div>
                )}

                <div className="flex gap-3">
                  <div
                    className={`flex-1 p-2 md:p-3 rounded-lg transition-all ${linkedCustomer ? 'bg-blue-50 border border-blue-200 cursor-pointer hover:bg-blue-100' : 'bg-gray-50'}`}
                    onClick={() => { if (linkedCustomer?.id) { onClose(); navigate(`/customer-management?customer=${linkedCustomer.id}`); } }}
                  >
                    {loadingCustomer ? (
                      <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                    ) : linkedCustomer ? (
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="font-semibold text-gray-900 truncate">{linkedCustomer.account_name || linkedCustomer.ledger_name || selectedEntry.customer_name}</span>
                        </div>
                        {linkedCustomer.contact_name && (
                          <div className="flex items-center gap-2 text-gray-600"><User className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{linkedCustomer.contact_name}</span></div>
                        )}
                        {(linkedCustomer.phone || linkedCustomer.contact_phone) && (
                          <div className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5 flex-shrink-0" /><span>{linkedCustomer.phone || linkedCustomer.contact_phone}</span></div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-blue-600 mt-1"><ExternalLink className="w-3 h-3" /><span>View customer record</span></div>
                      </div>
                    ) : (
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-gray-700">{selectedEntry.customer_name || 'N/A'}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Customer not found in database</div>
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedEntry.status === 'completed' ? 'bg-green-100 text-green-800' :
                        selectedEntry.status === 'failed' ? 'bg-red-100 text-red-800' :
                        selectedEntry.status === 'collection' ? 'bg-yellow-100 text-yellow-800' :
                        selectedEntry.status === 'stock_transfer' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedEntry.status === 'stock_transfer' ? 'Stock Transfer' : selectedEntry.status || 'pending'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <div
                      onClick={() => (photoLoading ? null : (entryPhoto ? setShowPhotoOptions(true) : triggerPhotoInput()))}
                      className="w-24 h-24 md:w-28 md:h-28 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-orange-500 transition-colors overflow-hidden relative"
                      style={{ borderColor: entryPhoto ? enconOrange : '#d1d5db' }}
                      data-testid="transport-new-view-photo-box"
                    >
                      {photoLoading ? (
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 mx-auto text-orange-500 animate-spin" />
                          <span className="text-[10px] text-gray-500 mt-1">Loading...</span>
                        </div>
                      ) : entryPhoto ? (
                        <img src={entryPhoto} alt="Entry" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center p-2">
                          <FileText className="w-8 h-8 mx-auto mb-1 text-gray-400" />
                          <span className="text-[10px] text-gray-500">Add Photo</span>
                        </div>
                      )}
                    </div>
                    {entryPhoto && !photoLoading && (
                      <Button onClick={() => setShowPhotoView(true)} className="text-xs py-1 px-3 rounded-lg" style={{ backgroundColor: enconOrange, color: 'white' }}>View</Button>
                    )}
                    <input id="transport-entry-photo-upload-input" type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs md:text-sm font-medium text-gray-700">Status Notes:</label>
                  <div className="flex gap-2">
                    <textarea
                      value={selectedEntry.status_note || ''}
                      onChange={(e) => {
                        if (!userPermissions || userPermissions?.can_edit_notes || userPermissions?.role === 'super_admin') {
                          setSelectedEntry({ ...selectedEntry, status_note: e.target.value });
                        }
                      }}
                      placeholder={!userPermissions || userPermissions?.can_edit_notes || userPermissions?.role === 'super_admin' ? 'Add notes...' : 'Notes (view only)'}
                      className="flex-1 p-2 border border-gray-300 rounded-xl resize-none text-sm"
                      rows={3}
                      readOnly={userPermissions && !(userPermissions?.can_edit_notes || userPermissions?.role === 'super_admin')}
                      data-testid="transport-new-view-notes-textarea"
                    />
                    {(!userPermissions || userPermissions?.can_edit_notes || userPermissions?.role === 'super_admin') && (
                      <Button onClick={() => updateEntryNotes(selectedEntry.status_note || '')} className="w-16 py-2.5 flex items-center justify-center rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: enconOrange }} data-testid="transport-new-view-notes-save-btn">
                        Save
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-xs md:text-sm font-medium text-gray-700 mb-1">Update Status:</div>
                  <div className="grid grid-cols-2 gap-3">
                    {(!userPermissions || userPermissions?.can_mark_complete || userPermissions?.role === 'super_admin') && (
                      <Button onClick={() => updateEntryStatus('complete')} className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 text-sm rounded-xl" disabled={selectedEntry.status === 'complete'} data-testid="transport-new-view-mark-complete-btn">
                        <Check className="w-4 h-4 mr-1.5" /> Complete
                      </Button>
                    )}
                    {(!userPermissions || userPermissions?.can_mark_failed || userPermissions?.role === 'super_admin') && (
                      <Button onClick={() => updateEntryStatus('failed')} className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 text-sm rounded-xl" disabled={selectedEntry.status === 'failed'} data-testid="transport-new-view-mark-failed-btn">
                        <AlertCircle className="w-4 h-4 mr-1.5" /> Failed
                      </Button>
                    )}
                  </div>
                  <Button onClick={() => updateEntryStatus('pending')} className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2.5 text-sm rounded-xl" disabled={selectedEntry.status === 'pending' || !selectedEntry.status} data-testid="transport-new-view-reset-status-btn">
                    Reset Status
                  </Button>
                </div>

                <div className="h-0.5 bg-gradient-to-r from-orange-500 to-pink-500"></div>

                <div className="space-y-2">
                  <Button onClick={() => setShowServiceIssueSection(!showServiceIssueSection)} className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-4 md:py-6 text-base md:text-lg rounded-2xl flex items-center justify-center" data-testid="transport-new-view-service-issue-toggle-btn">
                    <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    Service Issue
                    {serviceIssue && <span className="ml-2 bg-white text-yellow-600 px-2 py-0.5 rounded-full text-xs font-bold">Added</span>}
                  </Button>
                  {showServiceIssueSection && (
                    <div className="space-y-3 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
                      <div>
                        <label className="text-xs md:text-sm font-medium text-gray-700 block mb-1">Category:</label>
                        <select value={serviceIssueCategory} onChange={(e) => setServiceIssueCategory(e.target.value)} className="w-full p-2 border border-yellow-400 rounded-lg text-sm bg-white" data-testid="transport-new-view-service-issue-category-select">
                          <option value="">Select category...</option>
                          <option value="Operational">Operational</option>
                          <option value="Over Committed">Over Committed</option>
                          <option value="Resource">Resource</option>
                          <option value="Input">Input</option>
                          <option value="Customer">Customer</option>
                          <option value="CLEAR">Clear Service Issue</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs md:text-sm font-medium text-gray-700 block mb-1">Notes:</label>
                        <textarea value={serviceIssueNotes} onChange={(e) => setServiceIssueNotes(e.target.value)} placeholder="Describe the service issue..." className="w-full p-2 border border-yellow-400 rounded-lg resize-none text-sm" rows={2} data-testid="transport-new-view-service-issue-notes-textarea" />
                      </div>
                      <div className="flex gap-3">
                        <Button onClick={() => saveServiceIssue()} className="flex-1 text-white py-2.5 rounded-xl text-sm font-semibold" style={{ backgroundColor: enconOrange }} data-testid="transport-new-view-service-issue-save-btn">
                          {serviceIssue ? 'Update Issue' : 'Save Issue'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Right: Parsed Order View — slides in ─────────── */}
          <div style={{
            overflow: 'hidden',
            transition: 'max-width 320ms cubic-bezier(0.4,0,0.2,1), opacity 280ms ease',
            maxWidth: viewParsedOrder ? '780px' : '0px',
            opacity: viewParsedOrder ? 1 : 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {viewParsedOrder && (
              <div className="flex flex-col h-full" style={{ width: '780px', borderLeft: '1px solid #f5e6d3' }}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0" style={{ borderBottomColor: '#f5e6d3', background: '#fff8f0' }}>
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: enconOrange }}>Parsed Order</span>
                  <div className="flex items-center gap-1.5">
                    {allOrderNumbers.length > 1 && (
                      <div className="flex items-center gap-0.5 mr-1" data-testid="parsed-order-outer-nav">
                        <button
                          onClick={() => handleNavigateOrder(allOrderNumbers[(allOrderNumbers.indexOf(viewParsedOrder?.order_number) - 1 + allOrderNumbers.length) % allOrderNumbers.length])}
                          disabled={viewParsedOrderLoading}
                          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-orange-100 transition-colors disabled:opacity-40"
                          style={{ color: enconOrange }}
                          title="Previous order number"
                          data-testid="parsed-order-outer-prev-btn"
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleNavigateOrder(allOrderNumbers[(allOrderNumbers.indexOf(viewParsedOrder?.order_number) + 1) % allOrderNumbers.length])}
                          disabled={viewParsedOrderLoading}
                          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-orange-100 transition-colors disabled:opacity-40"
                          style={{ color: enconOrange }}
                          title="Next order number"
                          data-testid="parsed-order-outer-next-btn"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <button onClick={() => setViewParsedOrder(null)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-orange-100 transition-colors" style={{ color: enconOrange }} title="Hide order view">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => { setViewParsedOrder(null); onClose(); }} className="w-7 h-7 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity" style={{ backgroundColor: enconOrange }} title="Close">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <ParsedOrderViewModal
                    order={viewParsedOrder}
                    inline={true}
                    allOrderNumbers={allOrderNumbers}
                    onNavigateOrder={handleNavigateOrder}
                    navLoading={viewParsedOrderLoading}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Details Modal */}
      <Dialog open={showTransferDetails} onOpenChange={setShowTransferDetails}>
        <DialogContent className="max-w-3xl w-[95vw] bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <FileText className="w-5 h-5" />
              Transfer Details
            </DialogTitle>
          </DialogHeader>
          {(!selectedEntry?.linked_transfers || selectedEntry.linked_transfers.length === 0) ? (
            <div className="py-8 text-center text-gray-500 text-sm">No linked transfers on this entry.</div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-purple-600 mb-1">Collection From</div>
                <div className="text-lg font-bold text-gray-900">
                  {selectedEntry.linked_transfers[0].collection_branch_name
                    || selectedEntry.linked_transfers[0].branch_name
                    || transferDetailsBranch?.name
                    || transferDetailsBranch?.display_name
                    || (transferDetailsBranch?.branchCode ? `${transferDetailsBranch.branchCode} Warehouse` : selectedEntry.customer_name)}
                </div>
                <div className="text-sm text-gray-700 mt-1 whitespace-pre-line">
                  {selectedEntry.linked_transfers[0].collection_branch_address
                    || [transferDetailsBranch?.address1, transferDetailsBranch?.address2, transferDetailsBranch?.city, transferDetailsBranch?.postcode].filter(Boolean).join(', ')
                    || selectedEntry.postcode
                    || 'Address not available'}
                </div>
                {(selectedEntry.linked_transfers[0].collection_branch_contact || transferDetailsBranch?.default_contact || transferDetailsBranch?.contact) && (
                  <div className="text-xs text-gray-600 mt-2">
                    <span className="font-semibold">Contact:</span> {selectedEntry.linked_transfers[0].collection_branch_contact || transferDetailsBranch?.default_contact || transferDetailsBranch?.contact}
                  </div>
                )}
                {(selectedEntry.linked_transfers[0].collection_branch_phone || transferDetailsBranch?.phone) && (
                  <div className="text-xs text-gray-600">
                    <span className="font-semibold">Phone:</span> {selectedEntry.linked_transfers[0].collection_branch_phone || transferDetailsBranch?.phone}
                  </div>
                )}
              </div>
              {selectedEntry.linked_transfers.map((t, tIdx) => (
                <div key={t.id || tIdx} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-blue-700 text-sm">{t.transfer_ref || 'N/A'}</span>
                      {t.requested_by && <span className="text-xs text-gray-500">by {t.requested_by}</span>}
                    </div>
                    <span className="text-xs text-gray-500">
                      {t.items?.length || 0} item{(t.items?.length || 0) === 1 ? '' : 's'}
                      {t.total_weight ? ` · ${Number(t.total_weight).toFixed(1)} kg` : ''}
                    </span>
                  </div>
                  {(t.items || []).length === 0 ? (
                    <div className="px-4 py-3 text-xs text-gray-500">No items on this transfer.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold">Code</th>
                            <th className="text-left px-3 py-2 font-semibold">Description</th>
                            <th className="text-right px-3 py-2 font-semibold">Qty</th>
                            <th className="text-left px-3 py-2 font-semibold">Unit</th>
                            <th className="text-right px-3 py-2 font-semibold">Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(t.items || []).map((it, iIdx) => (
                            <tr key={iIdx} className="border-t border-gray-100">
                              <td className="px-3 py-2 font-mono text-xs text-gray-800">{it.product_code || '-'}</td>
                              <td className="px-3 py-2 text-gray-800">{it.description || '-'}</td>
                              <td className="px-3 py-2 text-right font-semibold">{it.quantity_required ?? it.quantity ?? 0}</td>
                              <td className="px-3 py-2 text-gray-600">{it.stock_unit || '-'}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{it.line_weight ? `${Number(it.line_weight).toFixed(1)} kg` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end pt-3 border-t mt-2">
            <Button variant="outline" onClick={() => setShowTransferDetails(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Options Modal */}
      <Dialog open={showPhotoOptions} onOpenChange={setShowPhotoOptions}>
        <DialogContent className="bg-white max-w-sm w-[90vw] p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: enconBlack }}>Photo Options</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <Button onClick={() => { setShowPhotoOptions(false); triggerPhotoInput(); }} className="w-full py-3 text-base rounded-xl" style={{ backgroundColor: enconOrange, color: 'white' }}>Take Again</Button>
            <Button onClick={async () => { setEntryPhoto(null); setShowPhotoOptions(false); await deletePhotoFromBackend(); }} className="w-full py-3 text-base bg-red-600 hover:bg-red-700 text-white rounded-xl">Remove Photo</Button>
            <Button onClick={() => setShowPhotoOptions(false)} className="w-full py-3 text-base bg-gray-600 hover:bg-gray-700 text-white rounded-xl">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Photo View Modal */}
      <Dialog open={showPhotoView} onOpenChange={setShowPhotoView}>
        <DialogContent className="bg-black max-w-4xl w-[95vw] h-[90vh] p-2" hideCloseButton>
          <div className="relative w-full h-full flex items-center justify-center">
            <img src={entryPhoto} alt="Full view" className="max-w-full max-h-full object-contain" />
            <Button onClick={() => setShowPhotoView(false)} className="absolute top-4 right-4 p-2 rounded-full" style={{ backgroundColor: enconOrange, color: 'white' }}>
              <X className="w-6 h-6" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransportEntryDetailModal;
